/**
 * MIONGA Partnership Form.
 * POSTs to /api/mionga/partnership. Uses REACT_APP_BACKEND_URL as required.
 * Executive minimal UI in the MIONGA Deep Navy · Oak · Gold palette.
 */
import React from "react";
import axios from "axios";
import { Send, ArrowUpRight, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTEREST_KEYS = ["hotels", "fine-dining", "retail", "aviation", "family-venues", "other"];

export function PartnershipForm({ tokens, t }) {
  const [form, setForm] = React.useState({
    name: "", company: "", role: "", country: "",
    email: "", phone: "", interest: "hotels", message: "",
  });
  const [state, setState] = React.useState({ status: "idle", error: "" });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setState({ status: "loading", error: "" });
    try {
      const payload = {
        name: form.name.trim(),
        company: form.company.trim(),
        role: form.role.trim() || null,
        country: form.country.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        interest: form.interest,
        message: form.message.trim(),
      };
      await axios.post(`${API}/mionga/partnership`, payload, { timeout: 15000 });
      setState({ status: "success", error: "" });
      setForm({ name: "", company: "", role: "", country: "", email: "", phone: "", interest: "hotels", message: "" });
    } catch (err) {
      setState({ status: "error", error: err?.response?.data?.detail || t.error });
    }
  };

  const fieldClass =
    "w-full bg-transparent border rounded-md px-3.5 py-2.5 text-[13px] text-white " +
    "placeholder:text-white/30 outline-none transition focus:ring-1";
  const fieldStyle = {
    borderColor: tokens.border,
    color: tokens.white,
  };
  const focusRing = `focus:border-[${tokens.gold}]`;

  if (state.status === "success") {
    return (
      <div data-testid="mionga-partnership-success"
        className="rounded-lg border p-8 lg:p-10 text-center"
        style={{
          borderColor: tokens.border,
          background: `linear-gradient(180deg, rgba(74,124,89,0.10) 0%, rgba(11,26,46,0.9) 100%)`,
        }}>
        <div className="mx-auto w-14 h-14 rounded-full flex items-center justify-center mb-4"
          style={{ background: `${tokens.gold}22`, color: tokens.gold }}>
          <CheckCircle2 size={26} strokeWidth={1.6} />
        </div>
        <div className="font-display font-semibold text-xl mb-2" style={{ color: tokens.white }}>
          {t.success}
        </div>
        <div className="text-[12px] uppercase tracking-[0.24em]" style={{ color: tokens.oakSoft }}>
          MIONGA · Partnership Desk
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} data-testid="mionga-partnership-form"
      className="rounded-lg border p-6 lg:p-8"
      style={{
        borderColor: tokens.border,
        background: "linear-gradient(180deg, rgba(17,31,53,0.85) 0%, rgba(11,26,46,0.95) 100%)",
      }}>
      <div className="mb-6">
        <div className="font-display font-semibold text-lg tracking-tight" style={{ color: tokens.white }}>
          {t.title}
        </div>
        <div className="text-[12.5px] mt-1.5" style={{ color: tokens.oakSoft }}>
          {t.lede}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
        <Field label={t.name} required>
          <input required value={form.name} onChange={set("name")}
            className={`${fieldClass} ${focusRing}`} style={fieldStyle}
            data-testid="mionga-form-name" />
        </Field>
        <Field label={t.company} required>
          <input required value={form.company} onChange={set("company")}
            className={`${fieldClass} ${focusRing}`} style={fieldStyle}
            data-testid="mionga-form-company" />
        </Field>
        <Field label={t.role}>
          <input value={form.role} onChange={set("role")}
            className={`${fieldClass} ${focusRing}`} style={fieldStyle}
            data-testid="mionga-form-role" />
        </Field>
        <Field label={t.country} required>
          <input required value={form.country} onChange={set("country")}
            className={`${fieldClass} ${focusRing}`} style={fieldStyle}
            data-testid="mionga-form-country" />
        </Field>
        <Field label={t.email} required>
          <input required type="email" value={form.email} onChange={set("email")}
            className={`${fieldClass} ${focusRing}`} style={fieldStyle}
            data-testid="mionga-form-email" />
        </Field>
        <Field label={t.phone}>
          <input value={form.phone} onChange={set("phone")}
            className={`${fieldClass} ${focusRing}`} style={fieldStyle}
            data-testid="mionga-form-phone" />
        </Field>
        <Field label={t.interest} className="sm:col-span-2">
          <select value={form.interest} onChange={set("interest")}
            className={`${fieldClass} ${focusRing} appearance-none cursor-pointer`}
            style={{ ...fieldStyle, background: "#0B1A2E" }}
            data-testid="mionga-form-interest">
            {INTEREST_KEYS.map((k) => (
              <option key={k} value={k} style={{ background: "#0B1A2E", color: "#FBFAF6" }}>
                {t.interestOptions[k]}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t.message} className="sm:col-span-2" required>
          <textarea required rows={4} value={form.message} onChange={set("message")}
            className={`${fieldClass} ${focusRing} resize-y`} style={fieldStyle}
            data-testid="mionga-form-message" />
        </Field>
      </div>

      {state.status === "error" && (
        <div className="mt-4 flex items-start gap-2 rounded-md border px-3 py-2.5 text-[12px]"
          style={{ borderColor: "#ef444455", background: "#ef444411", color: "#fecaca" }}
          data-testid="mionga-form-error">
          <AlertCircle size={14} className="mt-0.5 shrink-0" />
          <span>{state.error}</span>
        </div>
      )}

      <div className="mt-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <p className="text-[10.5px] uppercase tracking-[0.22em] font-medium max-w-md"
          style={{ color: tokens.oakSoft }}>
          {t.privacy}
        </p>
        <button type="submit" disabled={state.status === "loading"}
          data-testid="mionga-form-submit"
          className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-[11px] uppercase tracking-[0.20em] font-bold transition hover:translate-y-[-1px] disabled:opacity-70 disabled:cursor-not-allowed"
          style={{
            background: tokens.gold, color: tokens.navy,
            boxShadow: `0 16px 40px -12px ${tokens.gold}88`,
          }}>
          {state.status === "loading" ? (
            <><Loader2 size={13} className="animate-spin" /> {t.submitting}</>
          ) : (
            <><Send size={13} /> {t.submit} <ArrowUpRight size={13} /></>
          )}
        </button>
      </div>
    </form>
  );
}

const Field = ({ label, required, children, className = "" }) => (
  <label className={`block ${className}`}>
    <span className="block text-[10px] uppercase tracking-[0.24em] font-semibold mb-1.5 text-white/60">
      {label}{required && <span className="ml-1 text-[#D4A857]">*</span>}
    </span>
    {children}
  </label>
);

export default PartnershipForm;
